import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { CashFlow } from '@api/CashFlows/CashFlow';
import {
    AMOUNT_MODE_LABELS,
    RECURRING_STATUS_LABELS,
    RecurringExpense,
    RecurringStatus
} from '@api/RecurringExpenses/RecurringExpense';
import RecurringExpenseServ, { RECURRING_ACTIVITY_SECTION } from '@api/RecurringExpenses/RecurringExpenseServ';
import { buildActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';
import ExpenseGroupChip from '@components/Expenses/ExpenseGroupChip';
import ModalConfirmExpense from '@components/SalesReport/Expenses/ModalConfirmExpense';
import { currencyFormatter } from '@utils/Numbers';
import { formatPeriod } from '@utils/Expenses';
import { canCurrentUser } from '@utils/Permissions';
import { Capability, seesAllBranches } from '@utils/Permissions/Capabilities';
import { usePermissionSubject, useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';

interface RecurringExpensesPanelProps {
    /** Sucursal del filtro del Informe (`'all'` = las visibles del usuario). */
    branchOfficeId: string;
    onChanged: () => void;
}

/**
 * Gastos fijos (plan de Gastos §3.4, F4): lo que el aseo genera cada mes. Arriba,
 * los estimados por confirmar; abajo, las plantillas con pausar, reanudar, cambiar
 * el monto y eliminar. Pausar o eliminar no toca los meses ya generados.
 *
 * Lecturas por apertura: las plantillas (la colección, decenas) + los estimados
 * pendientes (`pendingConfirmation == true`, un puñado).
 */
const RecurringExpensesPanel: React.FunctionComponent<RecurringExpensesPanelProps> = ({ branchOfficeId, onChanged }) => {
    const dispatch = useDispatch();
    const branchOffices = useVisibleBranchOffices();
    const subject = usePermissionSubject();
    const [templates, setTemplates] = useState<RecurringExpense[]>([]);
    const [pending, setPending] = useState<CashFlow[]>([]);
    const [confirming, setConfirming] = useState<CashFlow | undefined>(undefined);
    const [editingAmount, setEditingAmount] = useState<{ id: string; value: string } | undefined>(undefined);
    const [loaded, setLoaded] = useState(false);

    const branches: 'all' | string[] = useMemo(() => {
        if (branchOfficeId !== 'all') return [branchOfficeId];
        return seesAllBranches(subject) ? 'all' : branchOffices.map((branch) => branch.id!);
    }, [branchOfficeId, branchOffices, subject]);
    const branchName = (id?: string) => branchOffices.find((branch) => branch.id === id)?.name || '—';

    const load = async () => {
        try {
            const [templatesData, pendingData] = await Promise.all([
                RecurringExpenseServ.getRecurringExpenses(branches),
                RecurringExpenseServ.getPendingInstances(branches)
            ]);
            setTemplates(
                templatesData.sort(
                    (a, b) => branchName(a.branchOfficeId).localeCompare(branchName(b.branchOfficeId)) || a.categoryName.localeCompare(b.categoryName)
                )
            );
            setPending(pendingData.sort((a, b) => (a.period || '').localeCompare(b.period || '')));
        } catch (error) {
            showAlertError(error);
        }
        setLoaded(true);
    };

    useEffect(() => {
        void load();
    }, [branches]);

    const changed = () => {
        void load();
        onChanged();
    };

    const setStatus = async (template: RecurringExpense, status: RecurringStatus) => {
        showLoader();
        try {
            await RecurringExpenseServ.setRecurringStatus(template, status);
            showAlertMessage(status === 'active' ? 'Gasto fijo reanudado' : status === 'paused' ? 'Gasto fijo pausado' : 'Gasto fijo eliminado');
            changed();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onDelete = (template: RecurringExpense) =>
        showIOSAlert({
            title: 'Eliminar gasto fijo',
            message: `"${template.description}" de ${branchName(template.branchOfficeId)} ya no se generará. Los meses ya registrados se quedan como están.`,
            buttons: [
                { text: 'Volver', onPress: () => {}, style: 'cancel' },
                { text: 'Eliminar', onPress: () => void setStatus(template, 'deleted'), style: 'destructive' }
            ]
        });

    const saveAmount = async (template: RecurringExpense) => {
        if (!editingAmount) return;
        showLoader();
        try {
            await RecurringExpenseServ.setRecurringAmount(template, Number(editingAmount.value));
            showAlertMessage('Monto actualizado: aplica desde el próximo mes');
            setEditingAmount(undefined);
            changed();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const openActivity = (template: RecurringExpense) => {
        const origin = buildActivityOrigin(template as unknown as Record<string, unknown>, {
            verb: 'creó el gasto fijo',
            noun: 'Gasto fijo creado',
            name: template.createdByName
        });
        dispatch(
            setActivityDrawer({ isOpen: true, section: RECURRING_ACTIVITY_SECTION, sectionId: template.id, derived: origin ? [origin] : [] })
        );
    };

    return (
        <Stack height="100%" minHeight={0} spacing={2} p={2} boxSizing="border-box" sx={{ overflowY: 'auto' }}>
            <Stack spacing={1} flexShrink={0}>
                <Typography variant="h6" fontWeight={600}>
                    Por confirmar
                </Typography>
                {pending.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {loaded ? 'No hay gastos del mes esperando su monto.' : 'Cargando…'}
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {pending.map((flow) => (
                            <Stack
                                key={flow.id}
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                border={1}
                                borderColor="divider"
                                borderRadius={2}
                                px={2}
                                py={1}
                            >
                                <ExpenseGroupChip group={flow.categoryGroup} label={flow.categoryName} />
                                <Typography flex={1}>
                                    {`${branchName(flow.branchOfficeId)} · ${flow.period ? formatPeriod(flow.period) : ''}`}
                                </Typography>
                                <Typography fontWeight={600}>{`${currencyFormatter(flow.amount)} (estimado)`}</Typography>
                                {canCurrentUser(Capability.RecurringExpenseManage, { branchOfficeId: flow.branchOfficeId }) && (
                                    <Button size="small" variant="contained" onClick={() => setConfirming(flow)}>
                                        Confirmar monto
                                    </Button>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Stack>

            <Stack spacing={1} flex={1} minHeight={0}>
                <Typography variant="h6" fontWeight={600}>
                    Gastos que se repiten cada mes
                </Typography>
                <Alert severity="info" sx={{ flexShrink: 0 }}>
                    Se crean al registrar un gasto fijo o variable y contestar que se repite. El aseo de las 5:00 a.m. registra cada mes
                    el siguiente.
                </Alert>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sucursal</TableCell>
                                <TableCell>Categoría</TableCell>
                                <TableCell>Descripción</TableCell>
                                <TableCell align="right">Monto</TableCell>
                                <TableCell>Cómo</TableCell>
                                <TableCell>Desde</TableCell>
                                <TableCell>Último mes</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {templates.map((template) => {
                                const canManage = canCurrentUser(Capability.RecurringExpenseManage, {
                                    branchOfficeId: template.branchOfficeId
                                });
                                const isEditing = editingAmount?.id === template.id;
                                return (
                                    <TableRow key={template.id} hover sx={{ opacity: template.status === 'paused' ? 0.6 : 1 }}>
                                        <TableCell>{branchName(template.branchOfficeId)}</TableCell>
                                        <TableCell>
                                            <ExpenseGroupChip group={template.categoryGroup} label={template.categoryName} />
                                        </TableCell>
                                        <TableCell>{template.description}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {isEditing ? (
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        autoFocus
                                                        value={editingAmount.value}
                                                        onChange={(event) => setEditingAmount({ id: template.id, value: event.target.value })}
                                                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                        sx={{ width: 140 }}
                                                    />
                                                    <Button size="small" onClick={() => void saveAmount(template)}>
                                                        Guardar
                                                    </Button>
                                                </Stack>
                                            ) : (
                                                currencyFormatter(template.amount)
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {AMOUNT_MODE_LABELS[template.amountMode]}
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {template.paidFromCash ? 'De la caja del turno' : 'Con otro dinero'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatPeriod(template.startPeriod)}</TableCell>
                                        <TableCell>{formatPeriod(template.lastPeriod)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={template.status === 'active' ? 'success' : 'default'}
                                                label={RECURRING_STATUS_LABELS[template.status]}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {canManage && template.amountMode === 'fixed' && !isEditing && (
                                                <Tooltip title="Cambiar el monto (desde el próximo mes)">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Cambiar el monto"
                                                        onClick={() => setEditingAmount({ id: template.id, value: String(template.amount) })}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canManage && template.status === 'active' && (
                                                <Tooltip title="Pausar">
                                                    <IconButton size="small" aria-label="Pausar" onClick={() => void setStatus(template, 'paused')}>
                                                        <PauseCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canManage && template.status === 'paused' && (
                                                <Tooltip title="Reanudar">
                                                    <IconButton size="small" aria-label="Reanudar" onClick={() => void setStatus(template, 'active')}>
                                                        <PlayCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canManage && (
                                                <Tooltip title="Eliminar">
                                                    <IconButton size="small" aria-label="Eliminar" onClick={() => onDelete(template)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Actividad">
                                                <IconButton size="small" aria-label="Actividad" onClick={() => openActivity(template)}>
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {loaded && templates.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Box py={2} textAlign="center">
                                            <Typography color="text.secondary">Todavía no hay gastos fijos.</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
            <ModalConfirmExpense expense={confirming} onClose={() => setConfirming(undefined)} onConfirmed={changed} />
        </Stack>
    );
};

export default RecurringExpensesPanel;
